import { useFetch } from '@/composables/useFetch';
import { useEnvStore } from '@/stores/envStore';
import type { CameraUploadPayload } from '@/types/camera';
import { Capacitor } from '@capacitor/core';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface UploadOptions<T = unknown> {
  endpoint: string;
  timeout?: number;
  additionalData?: Record<string, unknown>;
  fieldName?: string; // default: 'file'
  transport?: 'auto' | 'json-base64' | 'multipart'; // transport strategy
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
}

type UploadInput = File | CameraUploadPayload;

export function useFileUpload() {
  const { fetchData } = useFetch();
  const { apiUrl } = useEnvStore();
  const toast = useToast();
  const { t } = useI18n();
  const isUploading = ref(false);

  async function uploadFile<T = unknown>(input: UploadInput, options: UploadOptions<T>) {
    isUploading.value = true;

    try {
      const file = input instanceof File ? input : input.file;
      if (!file || file.size === 0) {
        throw new Error('Invalid file: empty or undefined');
      }

      // Decide transport strategy
      const isNative = Capacitor.isNativePlatform();
      let strategy = options.transport ?? 'auto';

      if (strategy === 'auto') {
        // For dish import, prefer base64 to ensure consistent processing
        strategy = options.endpoint.includes('/dishes/import/') ? 'json-base64' : isNative ? 'json-base64' : 'multipart';
      }

      let requestBody: FormData | Record<string, unknown>;

      if (strategy === 'multipart') {
        const formData = new FormData();
        const fieldName = options.fieldName || 'file';
        formData.append(fieldName, file, file.name);
        if (options.additionalData) {
          Object.entries(options.additionalData).forEach(([k, v]) => {
            const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
            formData.append(k, val);
          });
        }
        requestBody = formData;
      } else {
        // json-base64 strategy
        const base64Image = await convertFileToBase64(file);
        requestBody = {
          ...(options.additionalData || {}),
          base64_image: base64Image,
          file_name: file.name,
          mime_type: file.type,
          size: file.size,
        };
      }

      const { data, error } = await fetchData(`${apiUrl}${options.endpoint}`, 'POST', requestBody, options.timeout ?? 105000);
      console.log('FILE UPLOAD RESPONSE:', { data, error });
      if (error) {
        console.error('Upload failed:', error);
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('common.errors.upload') || 'Upload failed',
          life: 3000,
        });
        options.onError?.(error);
        return;
      }

      options.onSuccess?.(data as T);
    } catch (error) {
      console.error('Upload error:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('common.errors.upload') || 'Upload failed',
        life: 3000,
      });
      options.onError?.(error);
    } finally {
      isUploading.value = false;
    }
  }

  // Fonction pour convertir un fichier en base64
  function convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  return { uploadFile, isUploading };
}
